import React from "react";
import Icon from "../../../common/Icon";
import "./NoticesFilterCards.css";

interface NoticeCounts {
  total_licenses: number;
  active_licenses: number;
  in_progress: number;
  on_hold: number;
  resolved: number;
}

interface LicenseFilterCardsProps {
  counts?: NoticeCounts;
  isLoading?: boolean;
  onFilterChange?: (filterType: string | null) => void;
  activeFilter?: string | null;
  type?: "notices" | "litigations";
}

interface CardConfig {
  filterType: string | null;
  icon: string;
  label: string;
  countKey: keyof NoticeCounts;
  theme: "light_grey" | "light_blue" | "light_orange" | "light_red" | "light_green";
}

const getCardConfigs = (type: "notices" | "litigations" = "notices"): CardConfig[] => [
  {
    filterType: null,
    icon: "totalnotice",
    label: type === "notices" ? "Total Notices" : "Total Litigations",
    countKey: "total_licenses",
    theme: "light_grey",
  },
  {
    filterType: "open",
    icon: "noticeopen",
    label: "Open",
    countKey: "active_licenses",
    theme: "light_blue",
  },
  {
    filterType: "inprogress",
    icon: "noticeinprogress",
    label: "In Progress",
    countKey: "in_progress",
    theme: "light_orange",
  },
  {
    filterType: "on_hold",
    icon: "noticeonhold",
    label: "On Hold",
    countKey: "on_hold",
    theme: "light_red",
  },
  {
    filterType: "resolved",
    icon: "noticeresolvednotices",
    label: type === "notices" ? "Resolved Notices" : "Resolved Litigations",
    countKey: "resolved",
    theme: "light_green",
  },
];

const NoticesFilterCards: React.FC<LicenseFilterCardsProps> = ({
  counts = {
    total_licenses: 0,
    active_licenses: 0,
    in_progress: 0,
    on_hold: 0,
    resolved: 0,
  },
  isLoading = false,
  onFilterChange,
  activeFilter = null,
  type = "notices",
}) => {
  const CARD_CONFIGS = getCardConfigs(type);
  const displayValue = (value: number, config: CardConfig) => {
    if (isLoading) return "...";
    return value || value === 0 ? value : "-";
  };

  const handleCardClick = (filterType: string | null) => {
    if (onFilterChange) {
      onFilterChange(activeFilter === filterType ? null : filterType);
    }
  };

  const getCardClassName = (config: CardConfig) => {
    const isActive = activeFilter === config.filterType;
    const themeClass = isActive ? config.theme : "";
    return `cards d-flex align-items-center border-0 cursor-pointer ${themeClass}`;
  };

  const getIconContainerClassName = (config: CardConfig) => {
    return `icon-container ${config.theme}`;
  };

  return (
    <div className="d-flex mb-2 card-container" style={{ gap: "12px" }}>
      {CARD_CONFIGS.map((config) => (
        <div
          key={config.filterType || "total"}
          className={getCardClassName(config)}
          onClick={() => handleCardClick(config.filterType)}
        >
          <div className={getIconContainerClassName(config)}>
            <Icon name={config.icon} />
          </div>
          <div>
            <h4 className="fs-4 fw-semibold">
              {displayValue(counts[config.countKey], config)}
            </h4>
            <p className="m-0">{config.label}</p>
          </div>
        </div>
      ))}
    </div>
  );
};

export default NoticesFilterCards;
